import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, X, Minus, Maximize2, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import useSupportStore from '../stores/supportStore';
import useAuthStore from '../stores/authStore';
import { supportAPI } from '../services/api';

const FloatingSupport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated } = useAuthStore();
    const { 
        formData, setFormData, resetForm,
        isOpen, toggleOpen, 
        isMinimized, setMinimized 
    } = useSupportStore();
    
    const [loading, setLoading] = React.useState(false);
    const [status, setStatus] = React.useState({ type: '', message: '' });

    // Sync with user data if authenticated
    React.useEffect(() => {
        if (isAuthenticated && user && !formData.name && !formData.email) {
            setFormData({
                name: user.name || '',
                email: user.email || ''
            });
        }
    }, [isAuthenticated, user, formData.name, formData.email, setFormData]);

    const handleChange = (e) => {
        setFormData({ [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus({ type: '', message: '' });

        try {
            await supportAPI.submitFeedback(formData);
            setStatus({ type: 'success', message: 'Sent! Thank you for your feedback.' });
            // Only reset message, keep name/email
            setFormData({ message: '', category: 'Feedback' });
            
            setTimeout(() => {
                setStatus({ type: '', message: '' });
                toggleOpen();
            }, 3000);
        } catch (error) {
            setStatus({ 
                type: 'error', 
                message: error.response?.data?.message || 'Failed to send. Try again.' 
            });
        } finally {
            setLoading(false);
        }
    };

    // If already on the support page, don't show the popup (or only show the button)
    const isSupportPage = location.pathname === '/support';

    if (!isOpen) {
        return (
            <button 
                className="floating-support-btn" 
                onClick={toggleOpen}
                title="Support & Feedback"
            >
                <MessageSquare size={24} />
            </button>
        );
    }

    if (isSupportPage) return null;

    return (
        <div className="support-widget-container">
            <div className={`support-popup ${isMinimized ? 'minimized' : ''}`}>
                <div className="support-header" onClick={() => isMinimized && setMinimized(false)}>
                    <h3>
                        <MessageSquare size={18} />
                        Support & Feedback
                    </h3>
                    <div className="support-header-actions">
                        <button className="support-header-btn" onClick={(e) => { e.stopPropagation(); setMinimized(!isMinimized); }}>
                            {isMinimized ? <Maximize2 size={16} /> : <Minus size={16} />}
                        </button>
                        <button className="support-header-btn" onClick={toggleOpen}>
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {!isMinimized && (
                    <div className="support-body">
                        {status.message && (
                            <div className={`login-error ${status.type === 'success' ? 'success-message' : ''}`} 
                                 style={{ 
                                     backgroundColor: status.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                     color: status.type === 'success' ? '#047857' : '#b91c1c',
                                     marginBottom: '16px',
                                     padding: '10px',
                                     borderRadius: '8px',
                                     fontSize: '13px',
                                     display: 'flex',
                                     alignItems: 'center',
                                     gap: '8px'
                                 }}>
                                {status.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                                {status.message}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group" style={{ marginBottom: '12px' }}>
                                <label className="form-label" style={{ fontSize: '12px' }}>Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="form-input"
                                    style={{ padding: '8px 12px' }}
                                />
                            </div>
                            <div className="form-group" style={{ marginBottom: '12px' }}>
                                <label className="form-label" style={{ fontSize: '12px' }}>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="form-input"
                                    style={{ padding: '8px 12px' }}
                                />
                            </div>
                            <div className="form-group" style={{ marginBottom: '12px' }}>
                                <label className="form-label" style={{ fontSize: '12px' }}>Type</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    className="form-select"
                                    style={{ padding: '8px 12px' }}
                                >
                                    <option value="Feedback">Feedback</option>
                                    <option value="Bug Report">Bug Report</option>
                                    <option value="Feature Request">Feature</option>
                                    <option value="Support">Support</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label className="form-label" style={{ fontSize: '12px' }}>Message</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows="3"
                                    className="form-input"
                                    style={{ resize: 'none', padding: '8px 12px' }}
                                ></textarea>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn btn-primary"
                                style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
                            >
                                {loading ? 'Sending...' : 'Send Feedback'}
                            </button>
                            <div style={{ textAlign: 'center', marginTop: '12px' }}>
                                <button 
                                    type="button" 
                                    className="text-primary" 
                                    style={{ background: 'none', border: 'none', fontSize: '12px', cursor: 'pointer' }}
                                    onClick={() => { navigate('/support'); toggleOpen(); }}
                                >
                                    Go to full support page
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloatingSupport;
